import { Injectable, inject } from '@angular/core';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { PopoverController } from '@ionic/angular/standalone';
import { Observable, catchError, finalize, firstValueFrom, of, switchMap, throwError } from 'rxjs';
import {
  EvaluatedItemTypeIds,
  HourlyServiceIds,
  IAddUserEvaluationDto,
  IEvaluationOptions,
  IUserEvaluationDto,
  MonthlyServiceIds,
  ServerErrorCodes,
} from 'src/app/contracts';
import { Api } from 'src/app/core/common/api';
import { HttpError, InternalError } from 'src/app/core/contracts';
import { getServerError, hasErrorCode } from 'src/app/core/helpers/error.handler';
import { getPlatform } from 'src/app/core/helpers/utils';
import { EvaluationPopoverComponent } from 'src/app/shared/components/evaluation/evaluation.popover';

// ⚠️ Must stay in sync with EvaluationsRepository.CustomerEffortEvaluations (backend)
export const customerEffortEvaluations = [
  EvaluatedItemTypeIds.HourlyServiceAfterRequestingAndCompletingPayment,
  EvaluatedItemTypeIds.ChangeVisitDateServiceAfterChangeIsComplete,
  EvaluatedItemTypeIds.MonthlyServiceAfterRequestingAndCompletingPayment,
  EvaluatedItemTypeIds.MonthlyRenewServiceAfterSubmittingRenewRequest,
];

@Injectable({ providedIn: 'root' })
export class EvaluationData {
  private api = inject(Api);
  private browser = inject(InAppBrowser);
  private popoverCtrl = inject(PopoverController);
  private evaluationPopoverOpen = false;
  evaluationOpenedFromNotification = false;
  getVisitEvaluation(visitId: number) {
    return this.api.get<IUserEvaluationDto>(`evaluation/visit/${visitId}`);
  }
  getHourlyRequestEvaluation(requestId: number) {
    return this.api.get<IUserEvaluationDto>(`evaluation/hourly-request/${requestId}`);
  }
  getHourlyServiceEvaluation(serviceId: HourlyServiceIds) {
    return this.api.get<IUserEvaluationDto>(`evaluation/hourly-service/${serviceId}`);
  }
  getChangeVisitDateEvaluation() {
    return this.api.get<IUserEvaluationDto>(`evaluation/change-visit-date`);
  }
  getMonthlyServiceEvaluation(serviceId: MonthlyServiceIds) {
    return this.api.get<IUserEvaluationDto>(`evaluation/monthly-service/${serviceId}`);
  }
  getMonthlyRenewEvaluation(newRequestId: MonthlyServiceIds) {
    return this.api.get<IUserEvaluationDto>(`evaluation/monthly-renew/${newRequestId}`);
  }
  getMonthlyContractEvaluation(contractId: number) {
    return this.api.get<IUserEvaluationDto>(`evaluation/monthly-contract/${contractId}`);
  }
  getUserEvaluation(userEvaluationId: number) {
    return this.api.get<IUserEvaluationDto>(`evaluation/${userEvaluationId}`);
  }
  getCompletedEvaluation(userEvaluationId: number) {
    return this.api.get<IUserEvaluationDto>(`evaluation/${userEvaluationId}/details`);
  }
  getPendingEvaluation() {
    return this.api.get<number | null>(`evaluation/pending`);
  }
  submit(evaluation: IAddUserEvaluationDto) {
    evaluation.platformId = getPlatform();
    return this.api.post(`evaluation`, evaluation);
  }
  dismissEvaluation(userEvaluationId: number) {
    return this.api.post(`evaluation/${userEvaluationId}/dismiss`, {});
  }
  openEvaluation(loadEvaluation$: Observable<IUserEvaluationDto>, options: IEvaluationOptions = {}): Observable<boolean> {
    if (this.evaluationPopoverOpen) {
      return of(false);
    }
    this.evaluationPopoverOpen = true;
    return loadEvaluation$.pipe(
      catchError((err: HttpError | InternalError) => {
        if (options.failSilently) {
          return of(false);
        }

        const serverError = getServerError(err);
        const data = serverError?.details?.[0]?.data as { url?: string; userEvaluationId?: number };

        if (hasErrorCode(err, ServerErrorCodes.AlreadyEvaluated) && !options.ignoreAlreadyEvaluated && data?.userEvaluationId) {
          return of(data.userEvaluationId);
        }

        if (data?.url) {
          if (hasErrorCode(err, ServerErrorCodes.EvaluationNotSupportedByApp) && !options.ignoreUnsupportedEvaluations) {
            this.browser.create(data.url, '_system');
            return of(false);
          }
        }

        // Expired evaluations are silently ignored — the user simply won't see the evaluation prompt
        if (hasErrorCode(err, ServerErrorCodes.EvaluationExpired)) {
          return of(false);
        }

        return throwError(() => err);
      }),
      switchMap(async userEvaluation => {
        if (!userEvaluation) {
          return false;
        }

        // AlreadyEvaluated: userEvaluation is the ID — fetch completed evaluation and show read-only
        if (typeof userEvaluation === 'number') {
          try {
            const completedEvaluation = await firstValueFrom(this.getCompletedEvaluation(userEvaluation));
            await this.showEvaluationPopover(completedEvaluation, true);
          } catch {
            // Silently ignore — evaluation was already completed, nothing actionable
          }
          return false;
        }

        if (typeof userEvaluation !== 'object') {
          return false;
        }

        return this.showEvaluationPopover(userEvaluation, false);
      }),
      finalize(() => {
        this.evaluationPopoverOpen = false;
      })
    );
  }

  private async showEvaluationPopover(evaluation: IUserEvaluationDto, readOnly: boolean): Promise<boolean> {
    const cssClass = readOnly ? 'evaluation-popover evaluation-readonly' : 'evaluation-popover';
    const popover = await this.popoverCtrl.create({
      component: EvaluationPopoverComponent,
      componentProps: { evaluation, readOnly },
      cssClass,
    });
    await popover.present();
    const { data: hasCompletedEvaluation } = await popover.onDidDismiss<boolean>();
    if (!hasCompletedEvaluation && !readOnly) {
      this.dismissEvaluation(evaluation.id).subscribe();
    }
    return hasCompletedEvaluation;
  }
}
